import {
  filterByStation,
  getPartnerEvSales,
  getStationActivityMetrics,
  getStationDetails,
  getStations,
} from '@/services/station';

import { formatAddress } from '@/utils/stringUtils';

import type { TEntityOption } from '@/components/filter/types';

import type { TGetPartnerEvSalesParams, TGetStations } from '@/types/station';

import { stableParams, unwrapData } from './utils';

/** Params of the EV activity metrics call: period, dates, site or sites, sort and time zone. */
export type TStationActivityMetricsParams = Parameters<typeof getStationActivityMetrics>[0];

/**
 * Sites asked for by the opening call that lets the app open: the first 12 sites of the partner.
 *
 * Why 12: the picker list shows the 12 opening sites (then the backend search for anything
 * else), and the My Sites carousel shows its first 12 slides from them without a request. The
 * default pick also looks for the lookup sites among them, so no extra call is needed. The backend
 * costs about 130 ms per site returned, so 12 sites cost about 1.6 s before the app opens. A
 * larger number delays the whole portal; a smaller one makes the carousel load slides on demand
 * sooner and lets fewer lookup sites match.
 */
const OPENING_SITES = 12;

/**
 * Params of the portal's opening call (`stationListQuery`): the first `OPENING_SITES` sites of the
 * partner and the total site count (`totalHits`). A module constant, so the query key has the same
 * contents on every render. Shared by `PortalFilterProvider` (which runs the call) and
 * `isAppWideQuery` (which must recognise this query and never reset it).
 */
export const OPENING_SITES_PARAMS = { page: 0, size: OPENING_SITES, filter: {} };

/**
 * Keys of the station area. Every station query starts with `stationKeys.all`, so a save can mark
 * the whole area old with `queryClient.invalidateQueries(stationKeys.all)`.
 * - `detail(id)`: one full station (Site Details page, customer page, headers, Visits shell).
 * - `list(params)`: one page of the station list (the 12 opening sites of the portal, the sites
 *   restored by id for a picker chip). The Stations table and the My Sites cards have their own
 *   keys, added by the PRs that move them (PR 3 tables, PR 4 My Sites).
 * - `search(query)`: the site picker's backend search for one typed text.
 * - `activityMetrics(params)`: EV activity per day or period (Dashboard Transactions chart, Site
 *   Performance panels, EV Performance tab). `evSales(params)`: EV sales total of the Store
 *   Transactions overview. `activityMetricsAll()` and `evSalesAll()` are the prefixes of those two
 *   kinds, used by the page refresh control (`usePageRefresh`) to target them without params. Both read data that no station save changes; they sit under
 *   `stationKeys.all` only so one root covers the area, so a station save also marks them old and
 *   the ones on screen refresh quietly.
 * Invalidated by: `useUpdateStation`, `useUploadStationLogo` and `useCreateStation`
 * (`src/mutations/station.ts`), always the whole `stationKeys.all`.
 */
export const stationKeys = {
  all: ['station'] as const,
  lists: () => [...stationKeys.all, 'list'] as const,
  list: (params: Parameters<TGetStations>[0]) =>
    [...stationKeys.lists(), stableParams(params)] as const,
  details: () => [...stationKeys.all, 'detail'] as const,
  detail: (id: string) => [...stationKeys.details(), id] as const,
  activityMetricsAll: () => [...stationKeys.all, 'activityMetrics'] as const,
  activityMetrics: (params: TStationActivityMetricsParams) =>
    [...stationKeys.activityMetricsAll(), stableParams(params)] as const,
  evSalesAll: () => [...stationKeys.all, 'evSales'] as const,
  evSales: (params: TGetPartnerEvSalesParams) =>
    [...stationKeys.evSalesAll(), stableParams(params)] as const,
  searches: () => [...stationKeys.all, 'search'] as const,
  search: (query: string) => [...stationKeys.searches(), query] as const,
};

/**
 * Query for one full station.
 * What: resolves to the station (`response.data.data`: general info, hours, amenities, fuel, ...).
 * Why: the Site Details page, the customer page and both headers read the same saved station, so a
 * save refreshes every screen that shows it, and opening the page again shows it at once.
 * Key: `['station', 'detail', id]`. staleTime: app default.
 * Invalidated by: W1 `updateStation`, W2 `uploadStationLogo`, W3 `createStation`.
 * Constraint: pages must read this data directly; do not copy it into state (the form on the page
 * takes its start values from it once, see `rules/formik-patterns.md`).
 *
 * @param id Station id.
 */
export const stationDetailsQuery = (id: string) => ({
  queryKey: stationKeys.detail(id),
  queryFn: ({ signal }: { signal?: AbortSignal }) =>
    getStationDetails(id, signal).then((response) => unwrapData(response.data.data)),
});

/**
 * Query for one page of the station list.
 * What: resolves to `{ result, totalHits, hasMore }` (`response.data.data`).
 * Why: the portal reads its 12 opening sites and the site total from it (`PortalFilterProvider`)
 * and restores picker chips by id (`useStationsFilter`), so a station save or create refreshes them.
 * Key: `['station', 'list', params]`, params copied and with sorted station ids (`stableParams`).
 * staleTime: app default.
 * Invalidated by: W1, W2, W3.
 *
 * @param params Page, size and filter of the list call.
 */
export const stationListQuery = (params: Parameters<TGetStations>[0]) => {
  const stable = stableParams(params);

  return {
    queryKey: stationKeys.list(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getStations(stable, signal).then((response) => unwrapData(response.data.data)),
  };
};

/**
 * Query for the site picker's backend search (at most 100 sites of the partner).
 * What: resolves to picker options (`id`, `title` = site name, `details` = address).
 * Why: the pickers (portal, catalog popups, members) search the backend for what the operator
 * types. Saved per typed text, so the same text opens at once, and a site rename or create marks
 * every saved search old.
 * Key: `['station', 'search', query]`. staleTime: app default.
 * Invalidated by: W1, W2, W3.
 *
 * @param query Text typed in the picker (empty string lists the first sites).
 */
export const stationSearchQuery = (query: string) => ({
  queryKey: stationKeys.search(query),
  queryFn: ({ signal }: { signal?: AbortSignal }): Promise<TEntityOption[]> =>
    filterByStation(query, signal).then(({ data }) =>
      // An empty answer (`{}`) has no `stationFilterResult`: no sites match, so the list is empty.
      (data.data?.stationFilterResult ?? []).map(({ stationId, name, location }) => ({
        id: stationId,
        title: name,
        details: formatAddress(location),
      })),
    ),
});

/**
 * Query for the EV activity metrics of a period (sessions, customers, energy per day, plus totals).
 * What: resolves to the response body `data` (`{ activity | activities, total }`); the page maps it.
 * Why: the Dashboard Transactions chart (many sites) and the Site Performance panels (one site, with
 * sort) read the same endpoint. Every request value is in the key: period type, dates, site or
 * sites, sort column and direction, time zone. So a sort or filter change is one new key, makes one
 * request, and an old slow answer can never replace the rows of the new one.
 * Key: `['station', 'activityMetrics', params]`, site ids sorted, undefined values dropped.
 * staleTime: app default. Callers add `enabled` when there is no site yet.
 * Invalidated by: W1, W2, W3 (root `stationKeys.all`; the data itself does not change from a save).
 *
 * @param params Period type, dates, `stationId` or `stationIds`, optional sort, time zone offset.
 */
export const stationActivityMetricsQuery = (params: TStationActivityMetricsParams) => {
  const stable = stableParams(params);

  return {
    queryKey: stationKeys.activityMetrics(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getStationActivityMetrics(stable, signal).then((response) => unwrapData(response.data.data)),
  };
};

/**
 * Query for the EV sales of the picked sites in a date range (transaction count and total amount).
 * What: resolves to the whole response body (`{ data: { transactionCount, totalAmount } }`), not
 * the inner `data`: the page parser (`parseEvSalesSummary`) accepts both shapes.
 * Why: the Dashboard EV Sales card and the Store Transactions overview show it next to the store
 * numbers. One partner-wide call (`getPartnerEvSales`) covers one site or many.
 * Key: `['station', 'evSales', { stationIds, from, to }]` (ids sorted by `stableParams`, so the
 * same pick in another order shares the cache). No ids means every site of the partner.
 * staleTime: app default.
 * Invalidated by: W1, W2, W3 (root `stationKeys.all`; the data itself does not change from a save).
 *
 * @param params Site ids (optional) and date range.
 */
export const partnerEvSalesQuery = (params: TGetPartnerEvSalesParams) => {
  const stable = stableParams(params);

  return {
    queryKey: stationKeys.evSales(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getPartnerEvSales(stable, signal).then((response) => unwrapData(response.data)),
  };
};
